import { organizerSessionFetch } from "./api-organizer-session";

/** Relative **`/api/v1/quizzes`** (**GET** list/detail, **POST** draft, **PATCH** title, **POST publish**). */
export const QUIZZES_API_PATH = "/api/v1/quizzes";

/** Organizer mints a fresh participant **magic link** for one assignment (**roster**). */
export function postAssignmentInviteLink(
  quizId: string,
  assignmentId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/assignments/${encodeURIComponent(assignmentId)}/invite-link`;
  return organizerSessionFetch(url, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
  });
}

export function fetchQuizRecipients(
  quizId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  return organizerSessionFetch(
    `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/recipients`,
    {
      ...init,
      method: "GET",
      credentials: "same-origin",
      headers,
    },
  );
}

/** Organizer **GET** completed attempt transcript for one assignment. */
export function fetchQuizAssignmentTranscript(
  quizId: string,
  assignmentId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/assignments/${encodeURIComponent(assignmentId)}/transcript`;
  return organizerSessionFetch(url, {
    ...init,
    method: "GET",
    credentials: "same-origin",
    headers,
  });
}

export function fetchQuizDetail(
  quizId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  return organizerSessionFetch(`${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}`, {
    ...init,
    method: "GET",
    credentials: "same-origin",
    headers,
  });
}

export function postPublishQuizRecipients(
  quizId: string,
  recipients: string[],
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return organizerSessionFetch(`${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/publish`, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({ recipients }),
  });
}

/** Query parameters for **GET** **`/api/v1/quizzes`** list pagination. */
export type QuizzesListQuery = {
  limit?: number;
  cursorCreatedAt?: string;
  cursorQuizId?: string;
};

export function fetchQuizzesList(
  init?: RequestInit,
  query?: QuizzesListQuery,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  const params = new URLSearchParams();
  if (typeof query?.limit === "number" && query.limit > 0) {
    params.set("limit", String(query.limit));
  }
  if (
    typeof query?.cursorCreatedAt === "string" &&
    query.cursorCreatedAt !== "" &&
    typeof query?.cursorQuizId === "string" &&
    query.cursorQuizId !== ""
  ) {
    params.set("cursor_created_at", query.cursorCreatedAt);
    params.set("cursor_quiz_id", query.cursorQuizId);
  }
  const qs = params.toString();
  const url = qs === "" ? QUIZZES_API_PATH : `${QUIZZES_API_PATH}?${qs}`;
  return organizerSessionFetch(url, {
    ...init,
    method: "GET",
    credentials: "same-origin",
    headers,
  });
}

export function postCreateDraftQuiz(init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return organizerSessionFetch(QUIZZES_API_PATH, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({}),
  });
}

export function patchQuizTitle(
  quizId: string,
  title: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}`;
  return organizerSessionFetch(url, {
    ...init,
    method: "PATCH",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({ title }),
  });
}

export function postMaterialUploadUrl(
  quizId: string,
  body: {
    contentType: string;
    filename: string;
    byteSize: number;
  },
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/material/documents/upload-url`;
  return organizerSessionFetch(url, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(body),
  });
}

export function postQuizGeneration(
  quizId: string,
  body: Record<string, unknown>,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/generation`;
  return organizerSessionFetch(url, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(body),
  });
}

/** Clears a stuck **running** **generation_pipeline_status** (organizer escape hatch). */
export function postQuizGenerationPipelineReset(
  quizId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/generation/reset`;
  return organizerSessionFetch(url, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({}),
  });
}

export function patchQuizPool(
  quizId: string,
  body: {
    add?: Array<Record<string, unknown>>;
    edit?: Array<Record<string, unknown>>;
    delete?: string[];
  },
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/pool`;
  return organizerSessionFetch(url, {
    ...init,
    method: "PATCH",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(body),
  });
}

export function postMaterialFinalize(
  quizId: string,
  body: {
    trainingDocumentId: string;
    objectKey: string;
    contentType: string;
    byteSize: number;
  },
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/material/documents`;
  return organizerSessionFetch(url, {
    ...init,
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(body),
  });
}

export function deleteMaterialDocument(
  quizId: string,
  trainingDocumentId: string,
  init?: RequestInit,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  const url = `${QUIZZES_API_PATH}/${encodeURIComponent(quizId)}/material/documents/${encodeURIComponent(trainingDocumentId)}`;
  return organizerSessionFetch(url, {
    ...init,
    method: "DELETE",
    credentials: "same-origin",
    headers,
  });
}
